import { useCallback, useEffect, useState } from "react";
import type { AppPermissionName } from "../lib/permissionInstructions";

export type PermissionStatusValue = "granted" | "denied" | "prompt" | "unsupported";

/** Maps our small app-level permission set to the browser's own
 * PermissionDescriptor name — its own lookup so adding a future permission
 * (e.g. "microphone") only means one entry here plus one in
 * permissionInstructions.ts's AppPermissionName union. */
const QUERY_NAME: Record<AppPermissionName, PermissionName> = {
  geolocation: "geolocation",
  camera: "camera",
};

async function queryOne(name: AppPermissionName): Promise<PermissionStatusValue> {
  if (!navigator.permissions?.query) return "unsupported";
  try {
    const status = await navigator.permissions.query({ name: QUERY_NAME[name] });
    return status.state as PermissionStatusValue;
  } catch {
    // Some browsers (Safari, notably) throw rather than resolve for names
    // they don't support querying at all (e.g. "camera") — falls back to
    // whatever actually invoking the permission reports instead.
    return "unsupported";
  }
}

/**
 * Generic, reusable status check for any of our small set of app
 * permissions — runs once per mount (i.e. on every reload, since a reload
 * remounts everything) and again whenever `names` changes, then stays live
 * for the rest of the session via each PermissionStatus's own "change"
 * event, so flipping a permission in browser settings while the tab's
 * still open is caught too, not just on the next reload.
 *
 * To support a future permission: add it to AppPermissionName in
 * permissionInstructions.ts and to QUERY_NAME above — everything else
 * (this hook, the instruction copy) already generalizes over the union.
 */
export function usePermissionStatuses(names: AppPermissionName[]) {
  const [statuses, setStatuses] = useState<Partial<Record<AppPermissionName, PermissionStatusValue>>>({});
  const [loading, setLoading] = useState(true);
  const key = names.join(",");

  const check = useCallback(() => {
    setLoading(true);
    void Promise.all(names.map(async (n) => [n, await queryOne(n)] as const)).then((pairs) => {
      setStatuses(Object.fromEntries(pairs));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    check();
    if (!navigator.permissions?.query) return;
    let cancelled = false;
    const cleanups: Array<() => void> = [];
    names.forEach((n) => {
      navigator.permissions
        .query({ name: QUERY_NAME[n] })
        .then((status) => {
          if (cancelled) return;
          const handler = () => check();
          status.addEventListener("change", handler);
          cleanups.push(() => status.removeEventListener("change", handler));
        })
        .catch(() => {
          // Unsupported name for this browser — nothing to subscribe to; queryOne's fallback already covers it.
        });
    });
    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { statuses, loading, recheck: check };
}
